import { useMutation } from "@apollo/client";
import { InputControl, usePlatformConfig } from "@aurem/components";
import { Button, Text } from "@aurem/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthContainer } from "components/AuthContainer";
import ROUTES from "const/routes";
import { useI18n } from "providers/I18nProvider";
import type { FC } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { Link, useLocation, useParams } from "react-router-dom";
import { gql } from "types";
import { useSignOut } from "utils/auth";
import * as z from "zod";

const SignInWithSaml: FC = () => {
  const { language } = useParams();
  const location = useLocation();
  const theme = usePlatformConfig();
  const { t } = useI18n();
  const formSchema = z.object({
    email: z.string().email(t("apps.admin.validation.email.format")),
  });
  const signOut = useSignOut();
  const [signIn, { error, loading }] = useMutation(
    gql(/* GraphQL */ `
      mutation RedirectUrlToIdentityProvider(
        $email: String!
        $routeAfterAuth: String
      ) {
        redirectUserToIdentityProvider(
          email: $email
          routeAfterAuth: $routeAfterAuth
        ) {
          redirectUrl
        }
      }
    `),
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    // TODO(Jakub): an explicit signout would probably be better
    await signOut();
    const { data } = await signIn({
      variables: { ...values, routeAfterAuth: location.state?.from },
    });
    const redirectUrl =
      data?.redirectUserToIdentityProvider.redirectUrl ?? null;
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }

  return (
    <AuthContainer platform="admin">
      <div>
        <h2 className="font-medium text-2xl">
          {t("apps.admin.pages.signInWithSaml.page.signIn.title", theme.name)}
        </h2>
        <Text className="flex gap-1 mt-1">
          <span className="text-sm font-normal text-neutral-400">
            {t(
              "apps.admin.pages.signInWithSaml.page.signIn.donthaveAccount",
            )}{" "}
          </span>
          <Button
            as={Link}
            to={`/${language}/${ROUTES.SIGN_UP}`}
            variant="text"
            color="neutral"
            className="underline"
          >
            {t("apps.admin.pages.signInWithSaml.page.signIn.signUpLink")}
          </Button>
        </Text>
      </div>
      <FormProvider {...form}>
        <form
          className="flex flex-col gap-6"
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-5">
            <InputControl
              data-e2e-id="signInWithSaml__input__email"
              label={t(
                "apps.admin.pages.signInWithSaml.page.signIn.email.label",
              )}
              name="email"
              placeholder={t(
                "apps.admin.pages.signInWithSaml.page.signIn.email.placeholder",
              )}
              required
            />
            {error && (
              <Text weight="medium" size="sm" className="text-danger-600">
                {error.message}
              </Text>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <Button
              data-e2e-id="signInWithSaml__button_submit"
              loading={loading}
              type="submit"
            >
              {t("apps.admin.pages.signInWithSaml.page.signInWithSaml.signIn")}
            </Button>
            <div className="flex gap-1 justify-center">
              <span className="text-sm text-neutral-800">
                {t(
                  "apps.admin.pages.signInWithSaml.page.signIn.youCanAlso",
                )}{" "}
              </span>
              <Button
                as={Link}
                to={`/${language}/${ROUTES.SIGN_IN}`}
                variant="text"
                color="neutral"
                className="underline"
              >
                {t(
                  "apps.admin.pages.signInWithSaml.page.signInWithSaml.continueWithEmail",
                )}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
      <div className="flex justify-center">
        <Button
          as={Link}
          to={`/${language}/${ROUTES.PASSWORD_FORGOTTEN}`}
          variant="text"
          color="neutral"
          className="underline"
        >
          {t("apps.admin.pages.signInWithSaml.page.signIn.forgotPassword")}
        </Button>
      </div>
    </AuthContainer>
  );
};

export default SignInWithSaml;
